import type { ReactElement } from "react";
// eslint-disable-next-line no-restricted-imports
import { Menu as MenuBase } from "@base-ui/react/menu";
import { useRender } from "@base-ui/react/use-render";
import { Button } from "@design-system/Button/Button";
import { useCloseOnExclusiveOverlay } from "@design-system/overlay/lightDismiss";
import { useIsOverlayDeferred } from "@design-system/overlay/overlayDeferral";
import { MenuPopup } from "./MenuPopup";
import type { MenuRootProps, MenuTriggerProps } from "./Menu.types";

/**
 * The element that opens a menu, for triggers the root cannot infer: a
 * custom component that renders a `<button>` (`nativeButton`), a non-button
 * element (`nativeButton={false}`) or a hover-opened trigger (`openOnHover`).
 * Pass it as a root's `trigger`; Base UI merges its ref and handlers with the
 * `render` element's own.
 */
export const MenuTrigger = (props: MenuTriggerProps) => (
    <MenuBase.Trigger {...props} />
);

/**
 * A `Menu.Trigger`'s element without its Base UI root, for a deferred root:
 * its `render` with the same props, none of the trigger's behavior. A
 * disabled trigger stays disabled the way Base UI marks it: native
 * `disabled` on a `<button>`, `aria-disabled` on any other element.
 * Strips the trigger-only props of `@base-ui/react` 1.7.0; recheck the list
 * when bumping it, or new ones reach the DOM element.
 */
const DeferredMenuTrigger = ({
    render,
    nativeButton = true,
    disabled = false,
    handle,
    payload,
    delay,
    closeDelay,
    openOnHover,
    ...elementProps
}: MenuTriggerProps) =>
    useRender({
        render,
        defaultTagName: "button",
        state: { open: false, disabled },
        props: {
            ...elementProps,
            type: nativeButton ? "button" : undefined,
            disabled: nativeButton ? disabled : undefined,
            "aria-disabled": !nativeButton && disabled ? true : undefined,
        },
    });

const isMenuTriggerElement = (
    element: ReactElement,
): element is ReactElement<MenuTriggerProps> => element.type === MenuTrigger;

/** An `asChild` Button renders its child, so it only counts as a `<button>` without it. */
const isAsChildElement = ({ props }: ReactElement) =>
    typeof props === "object" &&
    props !== null &&
    "asChild" in props &&
    props.asChild === true;

const isNativeButtonElement = (element: ReactElement) =>
    element.type === "button" ||
    (element.type === Button && !isAsChildElement(element));

const renderTrigger = (trigger: ReactElement) =>
    isMenuTriggerElement(trigger) ? (
        trigger
    ) : (
        <MenuTrigger
            render={trigger}
            nativeButton={isNativeButtonElement(trigger)}
        />
    );

/**
 * An action menu: `trigger` opens a popup of items that run actions. Not
 * modal by default, closes when an item is pressed or an exclusive overlay
 * (a modal or drawer) opens, and returns focus to the trigger on close.
 * Under a deferring `OverlayDeferralContext` it renders `trigger` alone (a
 * `Menu.Trigger`'s `render` element), with no popup.
 */
export const MenuRoot = ({
    trigger,
    children,
    modal = false,
    side = "bottom",
    align = "start",
    sideOffset = 4,
    alignOffset,
    container,
    classNames,
    ...rootProps
}: MenuRootProps) => {
    const actionsRef = useCloseOnExclusiveOverlay();
    const isDeferred = useIsOverlayDeferred();

    if (isDeferred) {
        return isMenuTriggerElement(trigger) ? (
            <DeferredMenuTrigger {...trigger.props} />
        ) : (
            trigger
        );
    }

    return (
        <MenuBase.Root modal={modal} {...rootProps} actionsRef={actionsRef}>
            {renderTrigger(trigger)}
            <MenuPopup
                side={side}
                align={align}
                sideOffset={sideOffset}
                alignOffset={alignOffset}
                container={container}
                classNames={classNames}
            >
                {children}
            </MenuPopup>
        </MenuBase.Root>
    );
};
